// src\hooks\usePortfolio.js

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = '/api';

// =============================================
// PORTFOLIO CACHE (scoped per workType)
// =============================================
class PortfolioCache {
  constructor() {
    this.cache = new Map();
    this.duration = 5 * 60 * 1000; // 5 minutes
  }

  get(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.duration) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  set(key, data) {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  clear(key) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Clear all cache entries matching a pattern
   */
  clearPattern(pattern) {
    for (const key of Array.from(this.cache.keys())) {
      if (key.includes(pattern)) this.cache.delete(key);
    }
  }
}

const portfolioCache = new PortfolioCache();

// =============================================
// FALLBACK CATEGORIES (when API fails)
// =============================================
const FALLBACK_CATEGORIES = {
  portfolio: [
    { id: 'all', name: 'All Projects' },
    { id: 'branding', name: 'Brand Design' },
    { id: 'product', name: 'Product Visual' },
    { id: 'others', name: 'Other Works' },
  ],
  illustration: [
    { id: 'all', name: 'All Illustrations' },
    { id: 'character', name: 'Character Art' },
    { id: 'scene', name: 'Scene Art' },
    { id: 'concept', name: 'Concept Art' },
    { id: 'others', name: 'Other Works' },
  ],
};

// =============================================
// MAIN HOOK
// =============================================
export const usePortfolio = (workType = 'portfolio', options = {}) => {
  const {
    /**
     * autoLoad:
     *   true  → fetch on mount (preload)
     *   false → stay dormant until workType becomes a real value
     *
     * Default: true.
     */
    autoLoad = true,
  } = options;

  const { getIdToken, isAdmin } = useAuth();

  // A hook is dormant ONLY if autoLoad is false AND workType is empty
  const isDormant =
    !autoLoad &&
    (workType === null || workType === undefined || workType === '__dormant');

  // Normalize — always a valid string for refs
  const safeWorkType = isDormant ? '__dormant' : workType || 'portfolio';

  // =============================================
  // STATE
  // =============================================
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(!isDormant);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastDocId, setLastDocId] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // =============================================
  // REFS
  // =============================================
  const isMountedRef = useRef(true);
  const initialLoadDoneRef = useRef(false);
  const clickTrackingRef = useRef(new Map());
  const isLoadingAllRef = useRef(false);
  const fullDataLoadedRef = useRef(false);
  const currentWorkTypeRef = useRef(safeWorkType);
  const previousWorkTypeRef = useRef(safeWorkType);
  const requestIdRef = useRef(0);

  // =============================================
  // UNMOUNT CLEANUP
  // =============================================
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // =============================================
  // API REQUEST HELPER
  // =============================================
  const makeRequest = useCallback(
    async (
      action,
      method = 'GET',
      body = null,
      isFormData = false,
      requireAuth = false,
    ) => {
      try {
        const url = `${API_BASE_URL}/portfolio?action=${action}`;
        const options = {
          method,
          headers: {},
        };

        if (requireAuth) {
          const token = await getIdToken();
          if (!token) throw new Error('Authentication required');
          options.headers['Authorization'] = `Bearer ${token}`;
        }

        if (body) {
          if (isFormData) {
            options.body = body;
          } else {
            options.headers['Content-Type'] = 'application/json';
            options.body = JSON.stringify(body);
          }
        }

        const response = await fetch(url, options);
        const data = await response.json();

        if (!data.success) {
          throw new Error(data.message || `Action failed: ${action}`);
        }

        return data;
      } catch (error) {
        console.error(`[usePortfolio] ${action} error:`, error);
        throw error;
      }
    },
    [getIdToken],
  );

  // =============================================
  // LOAD ALL PROJECTS (scoped per workType)
  // =============================================
  const loadAllProjectsAtOnce = useCallback(async () => {
    if (currentWorkTypeRef.current === '__dormant') {
      console.log('💤 [usePortfolio] Dormant — skipping loadAllProjects');
      return [];
    }

    const myRequestId = ++requestIdRef.current;
    isLoadingAllRef.current = true;
    setLoading(true);
    setError(null);

    const wt = currentWorkTypeRef.current;

    try {
      const url = `list&category=all&type=all&limit=1000&getAll=true&workType=${wt}`;
      console.log(`📦 [usePortfolio:${wt}] Loading ALL projects`);

      const data = await makeRequest(url, 'GET', null, false, false);

      // Discard stale response if a newer request started
      if (myRequestId !== requestIdRef.current) {
        console.log(`🚫 [usePortfolio:${wt}] Discarding stale response`);
        return [];
      }

      if (!isMountedRef.current) return [];
      if (currentWorkTypeRef.current !== wt) return [];

      const newProjects = data.projects || [];
      const totalCountData = data.totalCount || newProjects.length;

      console.log(
        `📦 [usePortfolio:${wt}] Loaded ${newProjects.length} projects`,
      );

      setAllProjects(newProjects);
      setProjects(newProjects);
      setHasMore(false);
      setLastDocId(null);
      setTotalCount(totalCountData);
      fullDataLoadedRef.current = true;

      // Scoped cache key
      const cacheKey = `projects_${wt}_all_all`;
      portfolioCache.set(cacheKey, {
        projects: newProjects,
        hasMore: false,
        lastDocId: null,
        totalCount: totalCountData,
        isFullData: true,
      });

      initialLoadDoneRef.current = true;
      return newProjects;
    } catch (err) {
      console.error(`❌ [usePortfolio:${wt}] Failed to load projects:`, err);
      if (isMountedRef.current && myRequestId === requestIdRef.current) {
        setError(err.message);
      }
      return [];
    } finally {
      if (isMountedRef.current && myRequestId === requestIdRef.current) {
        setLoading(false);
        isLoadingAllRef.current = false;
      }
    }
  }, [makeRequest]);

  // =============================================
  // LOAD CATEGORIES (scoped per workType)
  // =============================================
  const loadCategories = useCallback(async () => {
    if (currentWorkTypeRef.current === '__dormant') {
      console.log('💤 [usePortfolio] Dormant — skipping loadCategories');
      return;
    }

    const wt = currentWorkTypeRef.current;
    const cacheKey = `categories_${wt}`;

    try {
      const cached = portfolioCache.get(cacheKey);
      if (cached) {
        if (isMountedRef.current) setCategories(cached);
        return;
      }

      const data = await makeRequest(
        `categories&workType=${wt}`,
        'GET',
        null,
        false,
        false,
      );

      if (isMountedRef.current && currentWorkTypeRef.current === wt) {
        const categoriesData = data.categories || [];
        setCategories(categoriesData);
        portfolioCache.set(cacheKey, categoriesData);
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
      if (isMountedRef.current && currentWorkTypeRef.current === wt) {
        setCategories(FALLBACK_CATEGORIES[wt] || FALLBACK_CATEGORIES.portfolio);
      }
    }
  }, [makeRequest]);

  // =============================================
  // LOAD PROJECTS (paginated, scoped per workType)
  // =============================================
  const loadProjects = useCallback(
    async (
      category = selectedCategory,
      type = selectedType,
      loadMore = false,
    ) => {
      if (!isMountedRef.current) return;
      if (currentWorkTypeRef.current === '__dormant') return;

      const wt = currentWorkTypeRef.current;

      try {
        if (!loadMore) {
          setLoading(true);
          setLastDocId(null);
        } else {
          setLoadingMore(true);
        }
        setError(null);

        const cacheKey = `projects_${wt}_${category}_${type}`;

        // Admin or full data: load ALL
        if (isAdmin && !loadMore) {
          await loadAllProjectsAtOnce();
          return;
        }

        // Check full data cache (scoped)
        if (!loadMore && !initialLoadDoneRef.current) {
          const fullCacheKey = `projects_${wt}_all_all`;
          const cached = portfolioCache.get(fullCacheKey);
          if (cached && cached.isFullData) {
            console.log(
              `⚡ [usePortfolio:${wt}] Using cached FULL data:`,
              cached.projects.length,
            );
            setAllProjects(cached.projects);
            setProjects(cached.projects);
            setHasMore(cached.hasMore || false);
            setLastDocId(cached.lastDocId || null);
            setTotalCount(cached.totalCount || cached.projects.length);
            fullDataLoadedRef.current = true;
            setLoading(false);
            initialLoadDoneRef.current = true;
            return;
          }

          const cachedPaginated = portfolioCache.get(cacheKey);
          if (cachedPaginated && !cachedPaginated.isFullData) {
            console.log(
              `⚡ [usePortfolio:${wt}] Using cached paginated data:`,
              cachedPaginated.projects.length,
            );
            setProjects(cachedPaginated.projects);
            setHasMore(cachedPaginated.hasMore || false);
            setLastDocId(cachedPaginated.lastDocId || null);
            setTotalCount(
              cachedPaginated.totalCount || cachedPaginated.projects.length,
            );
            setLoading(false);
            initialLoadDoneRef.current = true;
            return;
          }
        }

        let url = `list&category=${category}&type=${type}&limit=12&workType=${wt}`;
        if (loadMore && lastDocId) {
          url += `&lastDocId=${lastDocId}`;
        }

        console.log(`📦 [usePortfolio:${wt}] Fetching projects: ${url}`);
        const data = await makeRequest(url, 'GET', null, false, false);

        if (!isMountedRef.current) return;
        if (currentWorkTypeRef.current !== wt) return;

        const newProjects = data.projects || [];
        const hasMoreData = data.hasMore || false;
        const newLastDocId = data.lastDocId || null;
        const totalCountData = data.totalCount || newProjects.length;

        setHasMore(hasMoreData);
        setLastDocId(newLastDocId);

        if (loadMore) {
          setProjects(prev => {
            const existingIds = new Set(prev.map(p => p.id));
            const uniqueNewProjects = newProjects.filter(
              p => !existingIds.has(p.id),
            );
            return [...prev, ...uniqueNewProjects];
          });
          setTotalCount(prev => Math.max(prev, totalCountData));
        } else {
          setProjects(newProjects);
          setTotalCount(totalCountData);
          portfolioCache.set(cacheKey, {
            projects: newProjects,
            hasMore: hasMoreData,
            lastDocId: newLastDocId,
            totalCount: totalCountData,
            isFullData: false,
          });
          initialLoadDoneRef.current = true;
        }
      } catch (err) {
        if (isMountedRef.current) setError(err.message);
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [
      selectedCategory,
      selectedType,
      lastDocId,
      makeRequest,
      isAdmin,
      loadAllProjectsAtOnce,
    ],
  );

  // =============================================
  // GET PROJECT COUNT
  // =============================================
  const getProjectCount = useCallback(() => {
    return allProjects?.length || 0;
  }, [allProjects]);

  // =============================================
  // LOAD MORE (Public only)
  // =============================================
  const loadMoreProjects = useCallback(() => {
    if (isDormant) return;

    if (isAdmin) {
      loadAllProjectsAtOnce();
      return;
    }

    if (!loadingMore && hasMore && !loading) {
      loadProjects(selectedCategory, selectedType, true);
    }
  }, [
    isDormant,
    loadingMore,
    hasMore,
    loading,
    selectedCategory,
    selectedType,
    loadProjects,
    isAdmin,
    loadAllProjectsAtOnce,
  ]);

  // =============================================
  // GET SINGLE PROJECT
  // =============================================
  const getProject = useCallback(
    async id => {
      try {
        const cached = portfolioCache.get(`project_${id}`);
        if (cached) return cached;

        const data = await makeRequest(
          `single&id=${id}`,
          'GET',
          null,
          false,
          false,
        );
        const project = data.project;

        portfolioCache.set(`project_${id}`, project);
        return project;
      } catch (error) {
        console.error('Failed to get project:', error);
        throw error;
      }
    },
    [makeRequest],
  );

  // =============================================
  // UPLOAD SINGLE IMAGE (Admin)
  // =============================================
  const uploadSingleImage = useCallback(
    async (file, tempId) => {
      if (!isAdmin) {
        throw new Error('Admin access required');
      }

      console.log(
        `⬆️ [usePortfolio] Uploading image: ${file.name} (${tempId})`,
      );
      try {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('tempId', tempId);

        const token = await getIdToken();
        const response = await fetch(
          `${API_BASE_URL}/portfolio?action=upload-image`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.message || 'Failed to upload image');
        }

        console.log('✅ [usePortfolio] Upload successful:', data.image);
        return data.image;
      } catch (error) {
        console.error('❌ [usePortfolio] Failed to upload image:', error);
        throw error;
      }
    },
    [isAdmin, getIdToken],
  );

  // =============================================
  // DELETE PROJECT IMAGE (Admin)
  // =============================================
  const deleteProjectImage = useCallback(
    async (projectId, imageIndex) => {
      if (!isAdmin) {
        throw new Error('Admin access required');
      }

      console.log(
        `🗑️ [usePortfolio] Deleting image from project ${projectId} at index ${imageIndex}`,
      );
      try {
        const token = await getIdToken();
        const response = await fetch(
          `${API_BASE_URL}/portfolio?action=delete-image&id=${projectId}&imageIndex=${imageIndex}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.message || 'Failed to delete image');
        }

        // Clear only current workType's project cache
        const wt = currentWorkTypeRef.current;
        portfolioCache.clearPattern(`projects_${wt}_`);
        portfolioCache.clear(`project_${projectId}`);

        await loadAllProjectsAtOnce();
        console.log('✅ [usePortfolio] Image deleted successfully');

        return data;
      } catch (error) {
        console.error('❌ [usePortfolio] Failed to delete image:', error);
        throw error;
      }
    },
    [isAdmin, getIdToken, loadAllProjectsAtOnce],
  );

  // =============================================
  // ADD PROJECT (Admin)
  // =============================================
  const addProject = useCallback(
    async (projectData, imageData) => {
      if (!isAdmin) {
        throw new Error('Admin access required');
      }

      console.log('📝 [usePortfolio] Adding project:', projectData.title);
      setActionLoading(true);
      try {
        const formData = new FormData();
        formData.append('title', projectData.title);
        formData.append('description', projectData.description);
        formData.append('category', projectData.category);
        formData.append('type', projectData.type || 'project');
        formData.append('workType', projectData.workType || 'portfolio');
        formData.append('client', projectData.client || '');
        formData.append('tags', JSON.stringify(projectData.tags || []));
        formData.append('featured', projectData.featured ? 'true' : 'false');
        formData.append(
          'thumbnailIndex',
          String(projectData.thumbnailIndex || 0),
        );

        if (projectData.seoTitle)
          formData.append('seoTitle', projectData.seoTitle);
        if (projectData.seoDescription)
          formData.append('seoDescription', projectData.seoDescription);
        if (projectData.seoKeywords)
          formData.append(
            'seoKeywords',
            JSON.stringify(projectData.seoKeywords),
          );

        if (imageData && imageData.length > 0) {
          if (typeof imageData[0] === 'string') {
            formData.append('imageUrls', JSON.stringify(imageData));
          } else {
            for (const file of imageData) {
              if (file instanceof File) {
                formData.append('images', file);
              }
            }
          }
        }

        const token = await getIdToken();
        const response = await fetch(
          `${API_BASE_URL}/portfolio?action=create`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.message || 'Failed to add project');
        }

        console.log(
          '✅ [usePortfolio] Project created successfully:',
          data.project,
        );

        // ✅ Invalidate ONLY the affected workType's cache.
        // The other workType's tab stays instant.
        const affectedWt = data.project?.workType || 'portfolio';
        portfolioCache.clearPattern(`projects_${affectedWt}_`);
        portfolioCache.clear(`categories_${affectedWt}`);

        // Reload current hook only
        await loadAllProjectsAtOnce();

        return data.project;
      } catch (error) {
        console.error('❌ [usePortfolio] Failed to add project:', error);
        throw error;
      } finally {
        setActionLoading(false);
      }
    },
    [isAdmin, getIdToken, loadAllProjectsAtOnce],
  );

  // =============================================
  // UPDATE PROJECT (Admin)
  // =============================================
  const updateProject = useCallback(
    async (id, projectData, imageData = null) => {
      if (!isAdmin) {
        throw new Error('Admin access required');
      }

      console.log(
        `📝 [usePortfolio] Updating project ${id}:`,
        projectData.title,
      );
      setActionLoading(true);
      try {
        const formData = new FormData();
        if (projectData.title) formData.append('title', projectData.title);
        if (projectData.description)
          formData.append('description', projectData.description);
        if (projectData.category)
          formData.append('category', projectData.category);
        if (projectData.type) formData.append('type', projectData.type);
        if (projectData.workType)
          formData.append('workType', projectData.workType);
        if (projectData.client !== undefined)
          formData.append('client', projectData.client);
        if (projectData.tags)
          formData.append('tags', JSON.stringify(projectData.tags));
        if (projectData.featured !== undefined) {
          formData.append('featured', projectData.featured ? 'true' : 'false');
        }
        if (projectData.thumbnailIndex !== undefined) {
          formData.append('thumbnailIndex', String(projectData.thumbnailIndex));
        }

        if (projectData.seoTitle)
          formData.append('seoTitle', projectData.seoTitle);
        if (projectData.seoDescription)
          formData.append('seoDescription', projectData.seoDescription);
        if (projectData.seoKeywords)
          formData.append(
            'seoKeywords',
            JSON.stringify(projectData.seoKeywords),
          );

        if (imageData && imageData.length > 0) {
          if (typeof imageData[0] === 'string') {
            formData.append('imageUrls', JSON.stringify(imageData));
          } else {
            for (const file of imageData) {
              if (file instanceof File) {
                formData.append('images', file);
              }
            }
          }
        }

        const token = await getIdToken();
        const response = await fetch(
          `${API_BASE_URL}/portfolio?action=update&id=${id}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.message || 'Failed to update project');
        }

        console.log('✅ [usePortfolio] Project updated successfully');

        // ✅ Clear both caches — workType could have changed on edit
        portfolioCache.clearPattern('projects_');
        portfolioCache.clearPattern('categories_');
        portfolioCache.clear(`project_${id}`);

        await loadAllProjectsAtOnce();

        return data.project;
      } catch (error) {
        console.error('❌ [usePortfolio] Failed to update project:', error);
        throw error;
      } finally {
        setActionLoading(false);
      }
    },
    [isAdmin, getIdToken, loadAllProjectsAtOnce],
  );

  // =============================================
  // DELETE PROJECT (Admin)
  // =============================================
  const deleteProject = useCallback(
    async id => {
      if (!isAdmin) {
        throw new Error('Admin access required');
      }

      console.log(`🗑️ [usePortfolio] Deleting project ${id}`);
      setActionLoading(true);
      try {
        const token = await getIdToken();
        const response = await fetch(
          `${API_BASE_URL}/portfolio?action=delete&id=${id}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.message || 'Failed to delete project');
        }

        // Optimistic local update
        setProjects(prev => prev.filter(p => p.id !== id));
        setAllProjects(prev => prev.filter(p => p.id !== id));
        setFilteredProjects(prev => prev.filter(p => p.id !== id));

        // ✅ Invalidate this workType's cache only
        const wt = currentWorkTypeRef.current;
        portfolioCache.clearPattern(`projects_${wt}_`);
        portfolioCache.clear(`project_${id}`);

        console.log('✅ [usePortfolio] Project deleted successfully');

        return true;
      } catch (error) {
        console.error('❌ [usePortfolio] Failed to delete project:', error);
        throw error;
      } finally {
        setActionLoading(false);
      }
    },
    [isAdmin, getIdToken],
  );

  // =============================================
  // DELETE TEMP IMAGE (Admin)
  // =============================================
  const deleteTempImage = useCallback(
    async publicId => {
      if (!isAdmin) {
        throw new Error('Admin access required');
      }

      console.log(`🗑️ [usePortfolio] Deleting temporary image: ${publicId}`);
      try {
        const token = await getIdToken();
        const response = await fetch(
          `${API_BASE_URL}/portfolio?action=delete-temp-image&publicId=${encodeURIComponent(publicId)}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.message || 'Failed to delete temporary image');
        }

        console.log('✅ [usePortfolio] Temporary image deleted successfully');
        return data;
      } catch (error) {
        console.error(
          '❌ [usePortfolio] Failed to delete temporary image:',
          error,
        );
        throw error;
      }
    },
    [isAdmin, getIdToken],
  );

  // =============================================
  // LIKE PROJECT (Public)
  // =============================================
  const likeProject = useCallback(
    async (id, wasLiked) => {
      try {
        const token = await getIdToken();
        const response = await fetch(`/api/portfolio?action=like&id=${id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId: 'anonymous' }),
        });
        const data = await response.json();
        if (data.success) {
          setProjects(prev =>
            prev.map(p => (p.id === id ? { ...p, likes: data.likes } : p)),
          );
          setAllProjects(prev =>
            prev.map(p => (p.id === id ? { ...p, likes: data.likes } : p)),
          );
        }
        return data;
      } catch (error) {
        console.error('Failed to like project:', error);
        throw error;
      }
    },
    [getIdToken],
  );

  // =============================================
  // SHARE PROJECT (Public)
  // =============================================
  const shareProject = useCallback(
    async (id, title, imageUrl, userName, userEmail) => {
      try {
        const response = await fetch(`/api/portfolio?action=share&id=${id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userName: userName || 'Guest',
            userEmail: userEmail || 'guest@example.com',
          }),
        });
        const data = await response.json();
        if (data.success) {
          setProjects(prev =>
            prev.map(p => (p.id === id ? { ...p, shares: data.shares } : p)),
          );
          setAllProjects(prev =>
            prev.map(p => (p.id === id ? { ...p, shares: data.shares } : p)),
          );
        }
        return data;
      } catch (error) {
        console.error('Failed to share project:', error);
        throw error;
      }
    },
    [],
  );

  // =============================================
  // TRACK VIEW (Public)
  // =============================================
  const trackView = useCallback(async id => {
    try {
      fetch(`/api/portfolio?action=view&id=${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }).catch(() => {});
    } catch (error) {
      // Silently fail
    }
  }, []);

  // =============================================
  // TRACK CLICK (Public)
  // =============================================
  const trackClick = useCallback(
    async id => {
      try {
        setProjects(prev => {
          const updated = prev.map(p =>
            p.id === id ? { ...p, clicks: (p.clicks || 0) + 1 } : p,
          );
          return updated;
        });
        setAllProjects(prev => {
          const updated = prev.map(p =>
            p.id === id ? { ...p, clicks: (p.clicks || 0) + 1 } : p,
          );
          return updated;
        });

        if (selectedProject && selectedProject.id === id) {
          setSelectedProject(prev => ({
            ...prev,
            clicks: (prev.clicks || 0) + 1,
          }));
        }

        const cacheKey = `project_${id}`;
        const cached = portfolioCache.get(cacheKey);
        if (cached) {
          portfolioCache.set(cacheKey, {
            ...cached,
            clicks: (cached.clicks || 0) + 1,
          });
        }

        clickTrackingRef.current.set(id, true);

        const response = await fetch(`/api/portfolio?action=click&id=${id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        clickTrackingRef.current.delete(id);

        if (data.success && data.clicks !== undefined) {
          setProjects(prev =>
            prev.map(p => (p.id === id ? { ...p, clicks: data.clicks } : p)),
          );
          setAllProjects(prev =>
            prev.map(p => (p.id === id ? { ...p, clicks: data.clicks } : p)),
          );
          if (selectedProject && selectedProject.id === id) {
            setSelectedProject(prev => ({ ...prev, clicks: data.clicks }));
          }
          const updatedCache = portfolioCache.get(cacheKey);
          if (updatedCache) {
            portfolioCache.set(cacheKey, {
              ...updatedCache,
              clicks: data.clicks,
            });
          }
        }

        return data;
      } catch (error) {
        console.error('Failed to track click:', error);
        clickTrackingRef.current.delete(id);
        return null;
      }
    },
    [selectedProject],
  );

  // =============================================
  // REFRESH (reload current workType)
  // =============================================
  const refresh = useCallback(async () => {
    if (isDormant) return;

    const wt = currentWorkTypeRef.current;
    console.log(`🔄 [usePortfolio:${wt}] Refreshing`);

    portfolioCache.clearPattern(`projects_${wt}_`);
    portfolioCache.clear(`categories_${wt}`);

    initialLoadDoneRef.current = false;
    fullDataLoadedRef.current = false;

    // Parallel fetch — faster than sequential
    await Promise.allSettled([loadAllProjectsAtOnce(), loadCategories()]);

    console.log(`✅ [usePortfolio:${wt}] Refresh complete`);
  }, [loadAllProjectsAtOnce, loadCategories, isDormant]);

  // =============================================
  // FILTER PROJECTS (by category + type)
  // =============================================
  useEffect(() => {
    let filtered = allProjects;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    if (selectedType !== 'all') {
      filtered = filtered.filter(p => p.type === selectedType);
    }

    setFilteredProjects(filtered);
  }, [allProjects, selectedCategory, selectedType]);

  // =============================================
  // INITIAL LOAD (mount only, cache-first)
  // =============================================
  useEffect(() => {
    if (isDormant) {
      console.log(
        `💤 [usePortfolio:${safeWorkType}] Dormant on mount — skipping fetch`,
      );
      setLoading(false);
      return;
    }

    console.log(
      `🚀 [usePortfolio:${safeWorkType}] Initial load (autoLoad=${autoLoad})`,
    );

    // Check memory cache first
    const cacheKey = `projects_${safeWorkType}_all_all`;
    const cached = portfolioCache.get(cacheKey);

    if (cached && cached.isFullData) {
      console.log(
        `⚡ [usePortfolio:${safeWorkType}] Instant load from cache (${cached.projects.length} items)`,
      );
      setAllProjects(cached.projects);
      setProjects(cached.projects);
      setTotalCount(cached.totalCount || cached.projects.length);
      setHasMore(cached.hasMore || false);
      setLastDocId(cached.lastDocId || null);
      fullDataLoadedRef.current = true;
      initialLoadDoneRef.current = true;
      setLoading(false);

      // Refresh categories in background if missing
      const cachedCats = portfolioCache.get(`categories_${safeWorkType}`);
      if (cachedCats) {
        setCategories(cachedCats);
      } else {
        loadCategories();
      }
      return;
    }

    // No cache → fetch
    Promise.allSettled([loadAllProjectsAtOnce(), loadCategories()]).catch(
      err => {
        console.error(
          `❌ [usePortfolio:${safeWorkType}] Initial fetch error:`,
          err,
        );
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =============================================
  // WORKTYPE CHANGE HANDLER — cache-first
  // =============================================
  useEffect(() => {
    if (previousWorkTypeRef.current === safeWorkType) return;

    console.log(
      `🔄 [usePortfolio] workType changed: ${previousWorkTypeRef.current} → ${safeWorkType}`,
    );

    previousWorkTypeRef.current = safeWorkType;
    currentWorkTypeRef.current = safeWorkType;

    // Invalidate any in-flight requests
    requestIdRef.current++;

    // ---------------------------------------------------
    // Try to restore from cache FIRST
    // ---------------------------------------------------
    const cacheKey = `projects_${safeWorkType}_all_all`;
    const cached = portfolioCache.get(cacheKey);

    if (cached && cached.isFullData) {
      console.log(
        `⚡ [usePortfolio:${safeWorkType}] Tab switch → instant from cache (${cached.projects.length})`,
      );
      setAllProjects(cached.projects);
      setProjects(cached.projects);
      setTotalCount(cached.totalCount || cached.projects.length);
      setHasMore(false);
      setLastDocId(null);
      setFilteredProjects(cached.projects);
      fullDataLoadedRef.current = true;
      initialLoadDoneRef.current = true;
      setLoading(false);
      setError(null);

      // Categories from cache too
      const cachedCats = portfolioCache.get(`categories_${safeWorkType}`);
      if (cachedCats) {
        setCategories(cachedCats);
      } else {
        loadCategories();
      }
      return;
    }

    // ---------------------------------------------------
    // No cache → reset and fetch
    // ---------------------------------------------------
    initialLoadDoneRef.current = false;
    fullDataLoadedRef.current = false;
    isLoadingAllRef.current = false;

    setAllProjects([]);
    setProjects([]);
    setFilteredProjects([]);
    setCategories([]);
    setTotalCount(0);
    setHasMore(false);
    setLastDocId(null);
    setSelectedCategory('all');
    setSelectedType('all');
    setSelectedProject(null);
    setError(null);

    if (safeWorkType === '__dormant') {
      setLoading(false);
      return;
    }

    console.log(`📦 [usePortfolio:${safeWorkType}] No cache — fetching fresh`);
    setLoading(true);
    Promise.allSettled([loadAllProjectsAtOnce(), loadCategories()]).catch(
      err => {
        console.error(
          `❌ [usePortfolio:${safeWorkType}] workType change fetch error:`,
          err,
        );
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeWorkType]);

  // =============================================
  // RETURN
  // =============================================
  return {
    // Data
    projects: filteredProjects,
    allProjects,
    filteredProjects,
    categories,
    totalCount,
    workType: safeWorkType === '__dormant' ? null : safeWorkType,
    isDormant,

    // Filters
    selectedCategory,
    selectedType,
    setSelectedCategory,
    setSelectedType,

    // Selection
    selectedProject,
    setSelectedProject,

    // Status
    loading,
    error,
    hasMore,
    loadingMore,
    actionLoading,

    // Helpers
    getProjectCount,

    // Actions
    loadProjects,
    loadMoreProjects,
    loadAllProjectsAtOnce,
    getProject,
    addProject,
    updateProject,
    deleteProject,
    deleteTempImage,
    uploadSingleImage,
    deleteProjectImage,
    likeProject,
    shareProject,
    trackView,
    trackClick,
    setProjects,
    refresh,
  };
};

export default usePortfolio;
